import { newMarkPosition, calculateBeatLength, calculateHeading, calculateDistance } from './GeoUtils';
import { calculateTriangleCourseMarks } from './calculateTriangleCourseMarks';
import { COURSE_TYPES, FINISH_OPTIONS, MARK_STATUS, MARKS, DEFAULT_STARTLINE_SCALE_FACTOR } from './constants';
import { trapezoid_course_angles } from './trapezoidCourseAngles';
import { getMarkColor, getMarkType, getMarkIcon } from './markUtils';
/**
 * Course calculation utility for sailing race course layout
 * Based on the LayTheCourseView implementation from the mobile app
 */



// Finish option constants
const FO_L_SF_L = 'Start finish together';
const FO_L_LRP_L = 'Leeward, Reach port';
const FO_L_LRS_L = 'Leeward, Reach starboard';
const FO_W_W_L = 'Windward';
const FO_W_WRP_L = 'Windward, Reach port';
const FO_W_WRS_L = 'Windward, Reach starboard';
const FO_IO_SFTRHB_L = 'Start finish together. Reach = half beat length';
const FO_IO_SSFRHB_L = 'Separate Start finish. Reach = half beat length';
const FO_IO_EBRHB_L = 'Equal beats. Reach = half beat length';
const FO_IO_R23B_L = 'Reach = 2/3 beat length';
const FO_IO_WEBR23B_L = 'Windward, Equal beats, Reach = 2/3 beat length';
const FO_IO_WEBR23BSF = 'Windward, Equal beats, Reach = 2/3 beat, Start finish together';

/**
 * Create a new mark with position calculation
 */
const createMark = (lat, lng, bearing, distance, label, status) => {
  const position = newMarkPosition(lat, lng, bearing, distance);
  return {
    key: `${label}_${position.lat}_${position.lng}`,
    label: label,
    name: label,
    lat: position.lat,
    lng: position.lng,
    status: status,
    actualLat: position.lat,
    actualLng: position.lng,
    color: getMarkColor(label),
    type: getMarkType(label)
  };
};

/**
 * Create a combined mark (line or gate) with both starboard and port positions
 * This matches the ServerInteraction.js behavior
 */
const createCombinedMark = (lat, lng, bearing, distance, label, markType, kind0, tack0, kind1, tack1, gateWidth = 0) => {
  const basePosition = newMarkPosition(lat, lng, bearing, distance);
  
  // For lines (start/finish), calculate starboard and port positions
  let sPosition = basePosition;
  let pPosition = basePosition;
  
  if (gateWidth > 0) {
    // Create gate positions
    sPosition = newMarkPosition(basePosition.lat, basePosition.lng, bearing + 90, gateWidth / 2);
    pPosition = newMarkPosition(basePosition.lat, basePosition.lng, bearing - 90, gateWidth / 2);
  } else if (markType === 'L') {
    // For start line, one mark is at base position, other is offset
    pPosition = newMarkPosition(basePosition.lat, basePosition.lng, bearing - 90, distance);
  }
  
  return {
    key: `${label}_${sPosition.lat}_${pPosition.lng}`,
    label: label,
    name: label,
    mark_type: markType,
    kind0: kind0,
    tack0: tack0,
    latitude0: sPosition.lat,
    actualLatitude0: sPosition.lat,
    longitude0: sPosition.lng,
    actualLongitude0: sPosition.lng,
    kind1: kind1,
    tack1: tack1,
    latitude1: pPosition.lat,
    actualLatitude1: pPosition.lat,
    longitude1: pPosition.lng,
    actualLongitude1: pPosition.lng,
    color: getMarkColor(label),
    type: getMarkType(label),
    status: MARK_STATUS.LIFTED
  };
};

/**
 * Create a single mark (buoy)
 */
const createSingleMark = (lat, lng, bearing, distance, label, kind, tack) => {
  const position = newMarkPosition(lat, lng, bearing, distance);
  
  return {
    key: `${label}_${position.lat}_${position.lng}`,
    label: label,
    name: label,
    mark_type: 'B', // Buoy
    kind0: kind,
    tack0: tack,
    latitude0: position.lat,
    actualLatitude0: position.lat,
    longitude0: position.lng,
    actualLongitude0: position.lng,
    kind1: '',
    tack1: '',
    latitude1: null,
    actualLatitude1: null,
    longitude1: null,
    actualLongitude1: null,
    color: getMarkColor(label),
    type: getMarkType(label),
    status: MARK_STATUS.LIFTED
  };
};

/**
 * Convert calculated marks to server format (mimicking ServerInteraction.js)
 * This combines marks like SS/SP, 1S/1P, etc. into single database entries
 */
const convertMarksToServerFormat = (marks) => {
  const serverMarks = [];
  let sequenceNumber = 0;
  
  // Helper function to get actual coordinates only if mark is DROPPED and has actual values
  const getActualCoordinate = (mark, coordinate) => {
    if (mark.status === MARK_STATUS.LIFTED) return null;
    if (coordinate === 'lat') {
      return (mark.actualLat !== undefined && mark.actualLat !== null) ? mark.actualLat : null;
    }
    if (coordinate === 'lng') {
      return (mark.actualLng !== undefined && mark.actualLng !== null) ? mark.actualLng : null;
    }
    return 0;
  };
  
  // Create a map for quick lookup of marks by label
  const marksMap = new Map();
  marks.forEach(mark => {
    marksMap.set(mark.label, mark);
  });

  // Combine SS/FP and SP/FS or SS and SP into one dict item if both exist
  if ((marksMap.has(MARKS.SS_FP) && marksMap.has(MARKS.SP_FS)) || (marksMap.has(MARKS.SS) && marksMap.has(MARKS.SP))) {
    const ssMark = marksMap.get(MARKS.SS_FP) || marksMap.get(MARKS.SS);
    const spMark = marksMap.get(MARKS.SP_FS) || marksMap.get(MARKS.SP);
    
    serverMarks.push({
      label: 'SS_SP',
      mark_type: 'L',
      kind0: 'C',
      tack0: 'S',
      latitude0: ssMark.lat,
      actual_latitude0: getActualCoordinate(ssMark, 'lat'),
      longitude0: ssMark.lng,
      actual_longitude0: getActualCoordinate(ssMark, 'lng'),
      kind1: 'S',
      tack1: 'P',
      latitude1: spMark.lat,
      actual_latitude1: getActualCoordinate(spMark, 'lat'),
      longitude1: spMark.lng,
      actual_longitude1: getActualCoordinate(spMark, 'lng'),
      sequence_number: sequenceNumber++
    });
    
    // Remove from map to avoid adding again
    marksMap.delete(MARKS.SS_FP);
    marksMap.delete(MARKS.SP_FS);
    marksMap.delete(MARKS.SS);
    marksMap.delete(MARKS.SP);
  }

  // Combine 1S and 1P into one dict item if both exist
  if (marksMap.has(MARKS.M1S) && marksMap.has(MARKS.M1P)) {
    const sMark = marksMap.get(MARKS.M1S);
    const pMark = marksMap.get(MARKS.M1P);

    serverMarks.push({
      label: '1S_1P',
      mark_type: 'G',
      kind0: '1',
      tack0: 'S',
      latitude0: sMark.lat,
      actual_latitude0: getActualCoordinate(sMark, 'lat'),
      longitude0: sMark.lng,
      actual_longitude0: getActualCoordinate(sMark, 'lng'),
      kind1: '1',
      tack1: 'P',
      latitude1: pMark.lat,
      actual_latitude1: getActualCoordinate(pMark, 'lat'),
      longitude1: pMark.lng,
      actual_longitude1: getActualCoordinate(pMark, 'lng'),
      sequence_number: sequenceNumber++
    });

    marksMap.delete(MARKS.M1S);
    marksMap.delete(MARKS.M1P);
  }

  // Add single mark 1 if it exists
  if (marksMap.has(MARKS.M1)) {
    const mark = marksMap.get(MARKS.M1);
    serverMarks.push({
      label: '1',
      mark_type: 'B',
      kind0: '1',
      tack0: 'P',
      latitude0: mark.lat,
      actual_latitude0: getActualCoordinate(mark, 'lat'),
      longitude0: mark.lng,
      actual_longitude0: getActualCoordinate(mark, 'lng'),
      kind1: '',
      tack1: '',
      latitude1: null,
      actual_latitude1: null,
      longitude1: null,
      actual_longitude1: null,
      sequence_number: sequenceNumber++
    });
    marksMap.delete(MARKS.M1);
  }

  // Add single mark 1A if it exists
  if (marksMap.has(MARKS.M1A)) {
    const mark = marksMap.get(MARKS.M1A);
    serverMarks.push({
      label: '1A',
      mark_type: 'B',
      kind0: '1A',
      tack0: 'P',
      latitude0: mark.lat,
      actual_latitude0: getActualCoordinate(mark, 'lat'),
      longitude0: mark.lng,
      actual_longitude0: getActualCoordinate(mark, 'lng'),
      kind1: '',
      tack1: '',
      latitude1: null,
      actual_latitude1: null,
      longitude1: null,
      actual_longitude1: null,
      sequence_number: sequenceNumber++
    });
    marksMap.delete(MARKS.M1A);
  }

  // Add single mark 2 if it exists
  if (marksMap.has(MARKS.M2)) {
    const mark = marksMap.get(MARKS.M2);
    serverMarks.push({
      label: '2',
      mark_type: 'B',
      kind0: '2',
      tack0: 'P',
      latitude0: mark.lat,
      actual_latitude0: getActualCoordinate(mark, 'lat'),
      longitude0: mark.lng,
      actual_longitude0: getActualCoordinate(mark, 'lng'),
      kind1: '',
      tack1: '',
      latitude1: null,
      actual_latitude1: null,
      longitude1: null,
      actual_longitude1: null,
      sequence_number: sequenceNumber++
    });
    marksMap.delete(MARKS.M2);
  }

  // Combine 3S and 3P into one dict item if both exist
  if (marksMap.has(MARKS.M3S) && marksMap.has(MARKS.M3P)) {
    const sMark = marksMap.get(MARKS.M3S);
    const pMark = marksMap.get(MARKS.M3P);

    serverMarks.push({
      label: '3S_3P',
      mark_type: 'G',
      kind0: '3',
      tack0: 'S',
      latitude0: sMark.lat,
      actual_latitude0: getActualCoordinate(sMark, 'lat'),
      longitude0: sMark.lng,
      actual_longitude0: getActualCoordinate(sMark, 'lng'),
      kind1: '3',
      tack1: 'P',
      latitude1: pMark.lat,
      actual_latitude1: getActualCoordinate(pMark, 'lat'),
      longitude1: pMark.lng,
      actual_longitude1: getActualCoordinate(pMark, 'lng'),
      sequence_number: sequenceNumber++
    });

    marksMap.delete(MARKS.M3S);
    marksMap.delete(MARKS.M3P);
  }

  // Add single mark 3 if it exists
  if (marksMap.has(MARKS.M3)) {
    const mark = marksMap.get(MARKS.M3);
    serverMarks.push({
      label: '3',
      mark_type: 'B',
      kind0: '3',
      tack0: 'P',
      latitude0: mark.lat,
      actual_latitude0: getActualCoordinate(mark, 'lat'),
      longitude0: mark.lng,
      actual_longitude0: getActualCoordinate(mark, 'lng'),
      kind1: '',
      tack1: '',
      latitude1: null,
      actual_latitude1: null,
      longitude1: null,
      actual_longitude1: null,
      sequence_number: sequenceNumber++
    });
    marksMap.delete(MARKS.M3);
  }

  // Combine 4S and 4P into one dict item if both exist
  if (marksMap.has(MARKS.M4S) && marksMap.has(MARKS.M4P)) {
    const sMark = marksMap.get(MARKS.M4S);
    const pMark = marksMap.get(MARKS.M4P);

    serverMarks.push({
      label: '4S_4P',
      mark_type: 'G',
      kind0: '4',
      tack0: 'S',
      latitude0: sMark.lat,
      actual_latitude0: getActualCoordinate(sMark, 'lat'),
      longitude0: sMark.lng,
      actual_longitude0: getActualCoordinate(sMark, 'lng'),
      kind1: '4',
      tack1: 'P',
      latitude1: pMark.lat,
      actual_latitude1: getActualCoordinate(pMark, 'lat'),
      longitude1: pMark.lng,
      actual_longitude1: getActualCoordinate(pMark, 'lng'),
      sequence_number: sequenceNumber++
    });

    marksMap.delete(MARKS.M4S);
    marksMap.delete(MARKS.M4P);
  }

  // Add single mark 4 if it exists
  if (marksMap.has(MARKS.M4)) {
    const mark = marksMap.get(MARKS.M4);
    serverMarks.push({
      label: '4',
      mark_type: 'B',
      kind0: '4',
      tack0: 'P',
      latitude0: mark.lat,
      actual_latitude0: getActualCoordinate(mark, 'lat'),
      longitude0: mark.lng,
      actual_longitude0: getActualCoordinate(mark, 'lng'),
      kind1: '',
      tack1: '',
      latitude1: null,
      actual_latitude1: null,
      longitude1: null,
      actual_longitude1: null,
      sequence_number: sequenceNumber++
    });
    marksMap.delete(MARKS.M4);
  }

  // Add single mark 5 if it exists
  if (marksMap.has(MARKS.M5)) {
    const mark = marksMap.get(MARKS.M5);
    serverMarks.push({
      label: '5',
      mark_type: 'B',
      kind0: '5',
      tack0: 'P',
      latitude0: mark.lat,
      actual_latitude0: getActualCoordinate(mark, 'lat'),
      longitude0: mark.lng,
      actual_longitude0: getActualCoordinate(mark, 'lng'),
      kind1: '',
      tack1: '',
      latitude1: null,
      actual_latitude1: null,
      longitude1: null,
      actual_longitude1: null,
      sequence_number: sequenceNumber++
    });
    marksMap.delete(MARKS.M5);
  }

  // Combine FS and FP into finish line if both exist
  if (marksMap.has(MARKS.FS) && marksMap.has(MARKS.FP)) {
    const fsMark = marksMap.get(MARKS.FS);
    const fpMark = marksMap.get(MARKS.FP);

    serverMarks.push({
      label: 'FS_FP',
      mark_type: 'L',
      kind0: 'F',
      tack0: 'S',
      latitude0: fsMark.lat,
      actual_latitude0: getActualCoordinate(fsMark, 'lat'),
      longitude0: fsMark.lng,
      actual_longitude0: getActualCoordinate(fsMark, 'lng'),
      kind1: 'F',
      tack1: 'P',
      latitude1: fpMark.lat,
      actual_latitude1: getActualCoordinate(fpMark, 'lat'),
      longitude1: fpMark.lng,
      actual_longitude1: getActualCoordinate(fpMark, 'lng'),
      sequence_number: sequenceNumber++
    });

    marksMap.delete(MARKS.FS);
    marksMap.delete(MARKS.FP);
  }

  return serverMarks;
};

/**
 * Calculate start line marks
 */
const startLine = (course, startLineWidth) => {
  let marks = [];

  marks.push(createMark(
    course.lat, 
    course.lng, 
    course.windDirection, 
    0, 
    course.finishType === FO_L_SF_L ? MARKS.SS_FP : MARKS.SS, 
    MARK_STATUS.DROPPED
  ));

  marks.push(createMark(
    course.lat, 
    course.lng, 
    course.windDirection - 90.0, 
    startLineWidth, 
    course.finishType === FO_L_SF_L ? MARKS.SP_FS : MARKS.SP, 
    MARK_STATUS.LIFTED
  ));

  return marks;
};

/**
 * Calculate windward mark (Mark 1)
 */
const mark1 = (course, startMP, legs) => {
  let marks = [];

  let m1m = createMark(
    Number(startMP.lat), 
    Number(startMP.lng), 
    course.windDirection, 
    legs.windwardLeg + course.leewardDistance, 
    MARKS.M1, 
    MARK_STATUS.LIFTED
  );

  if (course.gate1) {
    let gateWidth = course.averageBoatLength * course.gateWidth;
    marks.push(createMark(
      m1m.lat, 
      m1m.lng, 
      course.windDirection - 180.0 + 90, 
      gateWidth / 2, 
      MARKS.M1P, 
      MARK_STATUS.LIFTED
    ));
    marks.push(createMark(
      m1m.lat, 
      m1m.lng, 
      course.windDirection - 180.0 - 90, 
      gateWidth / 2, 
      MARKS.M1S, 
      MARK_STATUS.LIFTED
    ));
  } else {
    marks.push(m1m);
  }

  if (course.offset && !course.gate1) {
    let m1a = createMark(
      Number(m1m.lat), 
      Number(m1m.lng), 
      course.windDirection - course.offsetAngle, 
      course.offsetDistance, 
      MARKS.M1A, 
      MARK_STATUS.LIFTED
    );
    marks.push(m1a);
  }

  return marks;
};

const mark3 = (course, refPoint, m4m3, m4m3Angle) => {
  let marks = [];

  const refLat = Number(refPoint['lat']);
  const refLng = Number(refPoint['lng']);
  const mark3M = newMarkPosition(refLat, refLng, course.windDirection + m4m3Angle, m4m3);

  if (course.gate3) {
    marks.push(createMark(mark3M['lat'], mark3M['lng'], course.windDirection - 180.0 + 90, (course.averageBoatLength * course.gateWidth) / 2, MARKS.M3S, MARK_STATUS.LIFTED));

    marks.push(createMark(mark3M['lat'], mark3M['lng'],  course.windDirection - 180.0 - 90, (course.averageBoatLength * course.gateWidth) / 2, MARKS.M3P, MARK_STATUS.LIFTED));
  } else {
    marks.push(createMark(mark3M['lat'], mark3M['lng'], course.windDirection - 180.0, 0, MARKS.M3, MARK_STATUS.LIFTED));
  }

  return marks;
};

/**
 * Calculate leeward mark (Mark 4)
 */
const mark4 = (course, m4m) => {
  let marks = [];

  const leeLat = Number(m4m.lat);
  const leeLng = Number(m4m.lng);

  if (course.gate4) {
    marks.push(createMark(
      leeLat, 
      leeLng, 
      course.windDirection - 180.0 + 90, 
      (course.averageBoatLength * course.gateWidth) / 2, 
      MARKS.M4S, 
      MARK_STATUS.LIFTED
    ));

    marks.push(createMark(
      leeLat, 
      leeLng, 
      course.windDirection - 180.0 - 90, 
      (course.averageBoatLength * course.gateWidth) / 2, 
      MARKS.M4P, 
      MARK_STATUS.LIFTED
    ));
  } else {
    marks.push(createMark(
      leeLat, 
      leeLng, 
      course.windDirection - 180.0, 
      0, 
      MARKS.M4, 
      MARK_STATUS.LIFTED
    ));
  }

  return marks;
};

/**
 * Calculate finish line marks
 */
const finishLine = (course, startMP, m4m, legs, existingMarks) => {
  let marks = [];

  const leeLat = Number(m4m.lat);
  const leeLng = Number(m4m.lng);
  let finishMP;
  let mark;
  let fLat = 0;
  let fLng = 0;
  let finishLineLength = course.averageBoatLength * course.gateWidth;

  // Handle "Start finish together" case for Leeward/Windward courses
  if (course.finishType === FO_L_SF_L) {
    // Use EXACT coordinates from start line marks (already in existingMarks array)
    // Find SS_FP (or SS) and SP_FS (or SP) marks to get their precise coordinates
    const ssMark = existingMarks.find(m => m.label === MARKS.SS_FP || m.label === MARKS.SS);
    const spMark = existingMarks.find(m => m.label === MARKS.SP_FS || m.label === MARKS.SP);
    
    if (!ssMark || !spMark) {
      console.error('Start line marks not found for finish line calculation');
      return marks;
    }
    
    // CRITICAL: Leeward courses have DOWNWIND finish (line reverses)
    // Windward courses have UPWIND finish (line stays same)
    const isLeeward = course.type === COURSE_TYPES.T_LEEWARD;
    
    if (isLeeward) {
      // DOWNWIND finish: tack designation reverses (SS→FP, SP→FS)
      marks.push(createMark(
        ssMark.lat, 
        ssMark.lng, 
        0,  // bearing doesn't matter - we're using exact lat/lng
        0,  // distance doesn't matter - we're using exact lat/lng
        MARKS.FP,  // SS becomes FP (reversed)
        MARK_STATUS.LIFTED
      ));
      
      marks.push(createMark(
        spMark.lat, 
        spMark.lng, 
        0,  // bearing doesn't matter - we're using exact lat/lng
        0,  // distance doesn't matter - we're using exact lat/lng
        MARKS.FS,  // SP becomes FS (reversed)
        MARK_STATUS.LIFTED
      ));
    } else {
      // UPWIND finish: tack designation stays same (SS→FS, SP→FP)
      marks.push(createMark(
        ssMark.lat, 
        ssMark.lng, 
        0,  // bearing doesn't matter - we're using exact lat/lng
        0,  // distance doesn't matter - we're using exact lat/lng
        MARKS.FS,  // SS becomes FS (same)
        MARK_STATUS.LIFTED
      ));
      
      marks.push(createMark(
        spMark.lat, 
        spMark.lng, 
        0,  // bearing doesn't matter - we're using exact lat/lng
        0,  // distance doesn't matter - we're using exact lat/lng
        MARKS.FP,  // SP becomes FP (same)
        MARK_STATUS.LIFTED
      ));
    }
  }
  else if (course.finishType === FO_W_W_L) {
    let mfm = createMark(
      Number(startMP.lat), 
      Number(startMP.lng), 
      course.windDirection, 
      course.leewardDistance + legs.windwardLeg + course.finishDistance, 
      'FM', 
      MARK_STATUS.LIFTED
    );

    let mfs = createMark(
      Number(mfm.lat), 
      Number(mfm.lng), 
      course.windDirection + 90.0, 
      finishLineLength / 2, 
      MARKS.FS, 
      MARK_STATUS.LIFTED
    );
    marks.push(mfs);
    
    let mfp = createMark(
      Number(mfm.lat), 
      Number(mfm.lng), 
      course.windDirection - 90.0, 
      finishLineLength / 2, 
      MARKS.FP, 
      MARK_STATUS.LIFTED
    );
    marks.push(mfp);
  } 
  else if (course.finishType === FO_W_WRP_L) {
    if (course.offset) {
      let mark1 = newMarkPosition(
        Number(startMP.lat), 
        Number(startMP.lng), 
        course.windDirection, 
        course.leewardDistance + legs.windwardLeg
      );
      mark = newMarkPosition(
        Number(mark1.lat), 
        Number(mark1.lng), 
        course.windDirection - course.offsetAngle, 
        course.offsetDistance
      );
      finishMP = newMarkPosition(
        Number(mark.lat), 
        Number(mark.lng), 
        course.windDirection - 180 + course.finishAngle, 
        course.finishDistance
      );
    } else {
      mark = newMarkPosition(
        Number(startMP.lat), 
        Number(startMP.lng), 
        course.windDirection, 
        course.leewardDistance + legs.windwardLeg
      );
      finishMP = newMarkPosition(
        Number(mark.lat), 
        Number(mark.lng), 
        course.windDirection - 180 + course.finishAngle, 
        course.finishDistance
      );
    }
    fLat = Number(finishMP.lat);
    fLng = Number(finishMP.lng);

    marks.push(createMark(
      fLat, 
      fLng, 
      course.windDirection - 180 + course.finishAngle + 90.0, 
      finishLineLength / 2, 
      MARKS.FS, 
      MARK_STATUS.LIFTED
    ));
    marks.push(createMark(
      fLat, 
      fLng, 
      course.windDirection - 180 + course.finishAngle - 90.0, 
      finishLineLength / 2, 
      MARKS.FP, 
      MARK_STATUS.LIFTED
    ));
  } else if (course.finishType === FO_W_WRS_L) {
      if (course.offset) {
        let mark1 = newMarkPosition(Number(startMP['lat']), Number(startMP['lng']), course.windDirection, course.leewardDistance + legs['windwardLeg']);
        mark = newMarkPosition(Number(mark1['lat']), Number(mark1['lng']), course.windDirection - course.offsetAngle, course.offsetDistance);
        finishMP = newMarkPosition(Number(mark['lat']), Number(mark['lng']), course.windDirection - 180 - course.finishAngle, course.finishDistance);
      } else {
        mark = newMarkPosition(Number(startMP['lat']), Number(startMP['lng']), course.windDirection, course.leewardDistance + legs['windwardLeg']);
        finishMP = newMarkPosition(Number(mark['lat']), Number(mark['lng']), course.windDirection - 180 - course.finishAngle, course.finishDistance);
      }
      fLat = Number(finishMP['lat']);
      fLng = Number(finishMP['lng']);

      marks.push(createMark(fLat, fLng, course.windDirection - 180 - course.finishAngle + 90.0, finishLineLength / 2, MARKS.FS, MARK_STATUS.LIFTED));
      marks.push(createMark(fLat, fLng, course.windDirection - 180 - course.finishAngle - 90.0, finishLineLength / 2, MARKS.FP, MARK_STATUS.LIFTED));
  } else if (course.finishType === FO_L_LRP_L) {
      // Position of gate 4P
      mark = newMarkPosition(Number(m4m['lat']), Number(m4m['lng']), course.windDirection - 180.0 - 90, finishLineLength / 2,);
      // 100 deg is the direction of slalom course - lets use that
      finishMP = newMarkPosition(Number(mark['lat']), Number(mark['lng']), course.windDirection - 180 - 60, course.finishDistance);
      fLat = Number(finishMP['lat']);
      fLng = Number(finishMP['lng']);

      marks.push(createMark(fLat, fLng, course.windDirection - 180 - course.finishAngle + 90.0, finishLineLength / 2, MARKS.FS, MARK_STATUS.LIFTED));
      marks.push(createMark(fLat, fLng, course.windDirection - 180 - course.finishAngle - 90.0, finishLineLength / 2, MARKS.FP, MARK_STATUS.LIFTED));
  } else if (course.finishType === FO_L_LRS_L) {
      // Position of gate 4P
      mark = newMarkPosition(Number(m4m['lat']), Number(m4m['lng']), course.windDirection - 180.0 + 90, finishLineLength / 2,);
      // 100 deg is the direction of slalom course - lets use that
      finishMP = newMarkPosition(Number(mark['lat']), Number(mark['lng']), course.windDirection - 180 + 60, course.finishDistance);
      fLat = Number(finishMP['lat']);
      fLng = Number(finishMP['lng']);

      marks.push(createMark(fLat, fLng, course.windDirection - 180 + course.finishAngle + 90.0, finishLineLength / 2, MARKS.FS, MARK_STATUS.LIFTED));
      marks.push(createMark(fLat, fLng, course.windDirection - 180 + course.finishAngle - 90.0, finishLineLength / 2, MARKS.FP, MARK_STATUS.LIFTED));
  }

  return marks;
};

/**
 * Calculate trapezoid course with start/finish together and reach = half beat
 * course
 * mss - mark start starboard
 * msm - middle of start line
 * m4m - middle of mark4 (gate)
 */
function calculateTrapezoidStartFinishReachHalfBeat(course, mss, msm, m4m, legs) {
  let marks = [];
  const numWindLegs = String(course.windwardBeats);
  const totalLength = course.distance;
  const key = (course.spinnaker ? "60°, 120° " : "70°, 110° ") + course.finishType;
  const rLat = Number(m4m.lat);
  const rLng = Number(m4m.lng);

  marks = marks.concat(mark4(course, m4m));

  marks = marks.concat(mark1(course, msm, legs));

  const m2Head = course.windDirection + trapezoid_course_angles[key]['angle-m4-m2'];
  const m2Dist = totalLength * trapezoid_course_angles[key]['I'][numWindLegs]['scale-m4-m2'];
  marks.push(createMark(rLat, rLng, m2Head, m2Dist, MARKS.M2, MARK_STATUS.LIFTED));

  const m3mHead = course.windDirection + trapezoid_course_angles[key]['angle-m4-m3'];
  const m3mDist = totalLength * trapezoid_course_angles[key]['I'][numWindLegs]['scale-m4-m3'];
  const m3m = createMark(rLat, rLng, m3mHead, m3mDist, MARKS.M3, MARK_STATUS.LIFTED);
  
  if (course.gate3) {
    const m3sHead = course.windDirection - 180.0 + 90;
    const m3sDist = (course.averageBoatLength * course.gateWidth) / 2;
    marks.push(createMark(m3m.lat, m3m.lng, m3sHead, m3sDist, MARKS.M3S, MARK_STATUS.LIFTED));

    const m3pHead = course.windDirection - 180.0 - 90;
    const m3pDist = (course.averageBoatLength * course.gateWidth) / 2;
    marks.push(createMark(m3m.lat, m3m.lng, m3pHead, m3pDist, MARKS.M3P, MARK_STATUS.LIFTED));
  } else {
    marks.push(m3m);
  }

  // Create finish line marks (start finish together)
  const finishLineLength = course.numberOfBoats * course.averageBoatLength;
  const fsHead = (course.windDirection + 220) % 360;
  marks.push(createMark(mss.lat, mss.lng, fsHead, finishLineLength, MARKS.FS, MARK_STATUS.LIFTED));
  
  // Add FP mark (opposite side of finish line)
  const fpHead = (course.windDirection + 40) % 360;  // 180° opposite of fsHead
  marks.push(createMark(mss.lat, mss.lng, fpHead, 0, MARKS.FP, MARK_STATUS.LIFTED));

  return marks;
}

function calculateTrapezoidSeparateStartFinishReachHalfBeat(course, mss, m4m) {
  let marks = [];
  const numWindLegs = String(course.windwardBeats);
  // Reference point is m4m
  // TODO - calculate leg i.e. total length minus the leeward distance, finish distance and offset distance
  const totalLength = course.distance;
  // 60°, 120° = Spinnaker
  // 70°, 110° = Non-spinnaker
  const key = (course.spinnaker ? "60°, 120° " : "70°, 110° ") + course.finishType;
  const rLat = Number(m4m['lat']);
  const rLng = Number(m4m['lng']);

  marks = marks.concat(mark4(course, m4m));

  const m1Head = course.windDirection + trapezoid_course_angles[key]['angle-m4-m1'];
  const m1Dist = totalLength * trapezoid_course_angles[key]['I'][numWindLegs]['scale-m4-m1'];
  const m1m = createMark(rLat, rLng, m1Head, m1Dist, MARKS.M1, MARK_STATUS.LIFTED);
  if (course.gate1) {
    const m1sHead = course.windDirection + 90;
    const m1sDist = (course.averageBoatLength * course.gateWidth) / 2;
    marks.push(createMark(m1m['lat'], m1m['lng'], m1sHead, m1sDist, MARKS.M1S, MARK_STATUS.LIFTED));

    const m1pHead = course.windDirection - 90;
    const m1pDist = (course.averageBoatLength * course.gateWidth) / 2;
    marks.push(createMark(m1m['lat'], m1m['lng'], m1pHead, m1pDist, MARKS.M1P, MARK_STATUS.LIFTED));
  } else {
    marks.push(m1m);
    if (course.offset) {
      marks.push(createMark(m1m['lat'], m1m['lng'], course.windDirection - course.offsetAngle, course.offsetDistance, MARKS.M1A, MARK_STATUS.LIFTED));
    }
  }

  const m2Head = course.windDirection + trapezoid_course_angles[key]['angle-m4-m2'];
  const m2Dist = totalLength * trapezoid_course_angles[key]['I'][numWindLegs]['scale-m4-m2'];
  marks.push(createMark(rLat, rLng, m2Head, m2Dist, MARKS.M2, MARK_STATUS.LIFTED));

  const m3mHead = course.windDirection + trapezoid_course_angles[key]['angle-m4-m3'];
  const m3mDist = totalLength * trapezoid_course_angles[key]['I'][numWindLegs]['scale-m4-m3'];
  const m3m = createMark(rLat,rLng, m3mHead, m3mDist, MARKS.M3, MARK_STATUS.LIFTED);

  let m3p = null;
  if (course.gate3) {
    const m3sHead = course.windDirection - 180.0 + 90;
    const m3sDist = (course.averageBoatLength * course.gateWidth) / 2;
    marks.push(createMark(m3m['lat'], m3m['lng'], m3sHead, m3sDist, MARKS.M3S, MARK_STATUS.LIFTED));

    const m3pHead = course.windDirection - 180.0 - 90;
    const m3pDist = (course.averageBoatLength * course.gateWidth) / 2;
    m3p = createMark(m3m['lat'], m3m['lng'], m3pHead, m3pDist, MARKS.M3P, MARK_STATUS.LIFTED);
    marks.push(m3p);
  } else {
    marks.push(m3m);
    m3p = m3m;
  }

  const finishLineLength = course.numberOfBoats * course.averageBoatLength;

  const fmHead = course.windDirection + 110;
  const fmDist = course.finishDistance;

  const fm = createMark(m3p['lat'], m3p['lng'], fmHead, fmDist, MARKS.FP, MARK_STATUS.LIFTED);
  marks.push(fm);
  marks.push(createMark(fm['lat'], fm['lng'], fmHead + 90, finishLineLength, MARKS.FS, MARK_STATUS.LIFTED));

  return marks;
};

function calculateTrapezoidEqualBeatsReachHalf(course, mss, m4m) {
  let marks = [];
  const numWindLegs = String(course.windwardBeats);
  // Reference point is m4m
  // TODO - calculate leg i.e. total length minus the leeward distance, finish distance and offset distance
  const totalLength = course.distance;
  // 60°, 120° = Spinnaker
  // 70°, 110° = Non-spinnaker
  const key = (course.spinnaker ? "60°, 120° " : "70°, 110° ") + course.finishType;
  const rLat = Number(m4m['lat']);
  const rLng = Number(m4m['lng']);

  marks = marks.concat(mark4(course, m4m));

  const m1Head = course.windDirection + trapezoid_course_angles[key]['angle-m4-m1'];
  const m1Dist = totalLength * trapezoid_course_angles[key]['I'][numWindLegs]['scale-m4-m1'];
  const m1m = createMark(rLat, rLng, m1Head, m1Dist, MARKS.M1, MARK_STATUS.LIFTED);
  if (course.gate1) {
    const m1sHead = course.windDirection + 90;
    const m1sDist = (course.averageBoatLength * course.gateWidth) / 2;
    marks.push(createMark(m1m['lat'], m1m['lng'], m1sHead, m1sDist, MARKS.M1S, MARK_STATUS.LIFTED));

    const m1pHead = course.windDirection - 90;
    const m1pDist = (course.averageBoatLength * course.gateWidth) / 2;
    marks.push(createMark(m1m['lat'], m1m['lng'], m1pHead, m1pDist, MARKS.M1P, MARK_STATUS.LIFTED));
  } else {
    marks.push(m1m);
    if (course.offset) {
      marks.push(createMark(m1m['lat'], m1m['lng'], course.windDirection - course.offsetAngle, course.offsetDistance, MARKS.M1A, MARK_STATUS.LIFTED));
    }
  }

  const m2Head = course.windDirection + trapezoid_course_angles[key]['angle-m4-m2'];
  const m2Dist = totalLength * trapezoid_course_angles[key]['I'][numWindLegs]['scale-m4-m2'];
  marks.push(createMark(rLat, rLng, m2Head, m2Dist, MARKS.M2, MARK_STATUS.LIFTED));

  const m3mHead = course.windDirection - 180 - trapezoid_course_angles[key]['angle-m4-m3'];
  const m3mDist = totalLength * trapezoid_course_angles[key]['I'][numWindLegs]['scale-m4-m3'];
  const m3m = createMark(rLat,rLng, m3mHead, m3mDist, MARKS.M3, MARK_STATUS.LIFTED);

  let m3p = null;
  if (course.gate3) {
    const m3sHead = course.windDirection - 180.0 + 90;
    const m3sDist = (course.averageBoatLength * course.gateWidth) / 2;
    marks.push(createMark(m3m['lat'], m3m['lng'], m3sHead, m3sDist, MARKS.M3S, MARK_STATUS.LIFTED));

    const m3pHead = course.windDirection - 180.0 - 90;
    const m3pDist = (course.averageBoatLength * course.gateWidth) / 2;
    m3p = createMark(m3m['lat'], m3m['lng'], m3pHead, m3pDist, MARKS.M3P, MARK_STATUS.LIFTED);
    marks.push(m3p);
  } else {
    marks.push(m3m);
    m3p = m3m;
  }

  const finishLineLength = course.gateWidth * course.averageBoatLength;
  const fmHead = course.windDirection + 110;
  const fmDist = course.finishDistance;

  const fm = createMark(m3p['lat'], m3p['lng'], fmHead, fmDist, MARKS.FP, MARK_STATUS.LIFTED);
  marks.push(fm);
  marks.push(createMark(fm['lat'], fm['lng'], fmHead + 90, finishLineLength, MARKS.FS, MARK_STATUS.LIFTED));

  return marks;
};

function calculateTrapezoidReach23Beat(course, mss, m4m) {
  let marks = [];
  const numWindLegs = String(course.windwardBeats);
  // Reference point is m4m
  // TODO - calculate leg i.e. total length minus the leeward distance, finish distance and offset distance
  const totalLength = course.distance;
  // 60°, 120° = Spinnaker
  // 70°, 110° = Non-spinnaker
  const key = (course.spinnaker ? "60°, 120° " : "70°, 110° ") + course.finishType;
  const rLat = Number(m4m['lat']);
  const rLng = Number(m4m['lng']);

  marks = marks.concat(mark4(course, m4m));

  const m1Head = course.windDirection + trapezoid_course_angles[key]['angle-m4-m1'];
  const m1Dist = totalLength * trapezoid_course_angles[key]['I'][numWindLegs]['scale-m4-m1'];
  const m1m = createMark(rLat, rLng, m1Head, m1Dist, MARKS.M1, MARK_STATUS.LIFTED);
  if (course.gate1) {
    const m1sHead = course.windDirection + 90;
    const m1sDist = (course.averageBoatLength * course.gateWidth) / 2;
    marks.push(createMark(m1m['lat'], m1m['lng'], m1sHead, m1sDist, MARKS.M1S, MARK_STATUS.LIFTED));

    const m1pHead = course.windDirection - 90;
    const m1pDist = (course.averageBoatLength * course.gateWidth) / 2;
    marks.push(createMark(m1m['lat'], m1m['lng'], m1pHead, m1pDist, MARKS.M1P, MARK_STATUS.LIFTED));
  } else {
    marks.push(m1m);
    if (course.offset) {
      marks.push(createMark(m1m['lat'], m1m['lng'], course.windDirection - course.offsetAngle, course.offsetDistance, MARKS.M1A, MARK_STATUS.LIFTED));
    }
  }

  const m2Head = course.windDirection + trapezoid_course_angles[key]['angle-m4-m2'];
  const m2Dist = totalLength * trapezoid_course_angles[key]['I'][numWindLegs]['scale-m4-m2'];
  marks.push(createMark(rLat, rLng, m2Head, m2Dist, MARKS.M2, MARK_STATUS.LIFTED));

  const m3mHead = course.windDirection + trapezoid_course_angles[key]['angle-m4-m3'];
  const m3mDist = totalLength * trapezoid_course_angles[key]['I'][numWindLegs]['scale-m4-m3'];
  const m3m = createMark(rLat,rLng, m3mHead, m3mDist, MARKS.M3, MARK_STATUS.LIFTED);

  let m3p = null;
  if (course.gate3) {
    const m3sHead = course.windDirection - 180.0 + 90;
    const m3sDist = (course.averageBoatLength * course.gateWidth) / 2;
    marks.push(createMark(m3m['lat'], m3m['lng'], m3sHead, m3sDist, MARKS.M3S, MARK_STATUS.LIFTED));

    const m3pHead = course.windDirection - 180.0 - 90;
    const m3pDist = (course.averageBoatLength * course.gateWidth) / 2;
    m3p = createMark(m3m['lat'], m3m['lng'], m3pHead, m3pDist, MARKS.M3P, MARK_STATUS.LIFTED);
    marks.push(m3p);
  } else {
    marks.push(m3m);
    m3p = m3m;
  }

  const finishLineLength = course.gateWidth * course.averageBoatLength;
  const fmHead = course.windDirection + 110;;
  const fmDist = course.finishDistance;

  const fm = createMark(m3p['lat'], m3p['lng'], fmHead, fmDist, MARKS.FP, MARK_STATUS.LIFTED);
  marks.push(fm);
  marks.push(createMark(fm['lat'], fm['lng'], fmHead + 90, finishLineLength, MARKS.FS, MARK_STATUS.LIFTED));

  return marks;
};

function calculateWindwardEqualBeatsReach23Beat(course, mss, m4m) {
  let marks = [];
  const numWindLegs = String(course.windwardBeats);
  // Reference point is m4m
  // TODO - calculate leg i.e. total length minus the leeward distance, finish distance and offset distance
  const totalLength = course.distance;
  // 60°, 120° = Spinnaker
  // 70°, 110° = Non-spinnaker
  const key = (course.spinnaker ? "60°, 120° " : "70°, 110° ") + course.finishType;
  const rLat = Number(m4m['lat']);
  const rLng = Number(m4m['lng']);

  marks = marks.concat(mark4(course, m4m));

  const m1Head = course.windDirection + trapezoid_course_angles[key]['angle-m4-m1'];
  const m1Dist = totalLength * trapezoid_course_angles[key]['I'][numWindLegs]['scale-m4-m1'];
  const m1m = createMark(rLat, rLng, m1Head, m1Dist, MARKS.M1, MARK_STATUS.LIFTED);
  if (course.gate1) {
    const m1sHead = course.windDirection + 90;
    const m1sDist = (course.averageBoatLength * course.gateWidth) / 2;
    marks.push(createMark(m1m['lat'], m1m['lng'], m1sHead, m1sDist, MARKS.M1S, MARK_STATUS.LIFTED));

    const m1pHead = course.windDirection - 90;
    const m1pDist = (course.averageBoatLength * course.gateWidth) / 2;
    marks.push(createMark(m1m['lat'], m1m['lng'], m1pHead, m1pDist, MARKS.M1P, MARK_STATUS.LIFTED));
  } else {
    marks.push(m1m);
    if (course.offset) {
      marks.push(createMark(m1m['lat'], m1m['lng'], course.windDirection - course.offsetAngle, course.offsetDistance, MARKS.M1A, MARK_STATUS.LIFTED));
    }
  }

  const m2Head = course.windDirection + trapezoid_course_angles[key]['angle-m4-m2'];
  const m2Dist = totalLength * trapezoid_course_angles[key]['I'][numWindLegs]['scale-m4-m2'];
  marks.push(createMark(rLat, rLng, m2Head, m2Dist, MARKS.M2, MARK_STATUS.LIFTED));

  const m3mHead = course.windDirection + trapezoid_course_angles[key]['angle-m4-m3'];
  const m3mDist = totalLength * trapezoid_course_angles[key]['I'][numWindLegs]['scale-m4-m3'];
  const m3m = createMark(rLat,rLng, m3mHead, m3mDist, MARKS.M3, MARK_STATUS.LIFTED);

  if (course.gate3) {
    const m3sHead = course.windDirection - 180.0 + 90;
    const m3sDist = (course.averageBoatLength * course.gateWidth) / 2;
    marks.push(createMark(m3m['lat'], m3m['lng'], m3sHead, m3sDist, MARKS.M3S, MARK_STATUS.LIFTED));

    const m3pHead = course.windDirection - 180.0 - 90;
    const m3pDist = (course.averageBoatLength * course.gateWidth) / 2;
    const m3p = createMark(m3m['lat'], m3m['lng'], m3pHead, m3pDist, MARKS.M3P, MARK_STATUS.LIFTED);
    marks.push(m3p);
  } else {
    marks.push(m3m);
  }

  const m5Head = course.windDirection + trapezoid_course_angles[key]['angle-m4-m5'];
  const m5Dist = totalLength * trapezoid_course_angles[key]['I'][numWindLegs]['scale-m4-m5'];
  const m5p = createMark(rLat, rLng, m5Head, m5Dist, MARKS.M5, MARK_STATUS.LIFTED);
  marks.push(m5p);

  // Tables in rulebook incorrect - calculate the finish line as a percentage of the distance
  // between 5m and sm
  const finishLineLength = course.gateWidth * course.averageBoatLength;
  const fmHead = course.windDirection;
  const m4mm5pDist = calculateDistance(rLat, rLng, m5p['lat'], m5p['lng']);
  // Get the distance between SM and 5P and set the finish line 1/3 from 5p
  const fmDist = (m4mm5pDist - course.leewardDistance) * 0.50;
  //const fmDist = totalLength * trapezoid_course_angles[key]['I'][numWindLegs]['scale-m5-fm'];

  const fm = newMarkPosition(m5p['lat'], m5p['lng'], fmHead, fmDist);
  marks.push(createMark(fm['lat'], fm['lng'], fmHead - 90, finishLineLength / 2, MARKS.FP, MARK_STATUS.LIFTED));
  marks.push(createMark(fm['lat'], fm['lng'], fmHead + 90, finishLineLength / 2, MARKS.FS, MARK_STATUS.LIFTED));

  return marks;
};

function calculateWindwardEqualBeatsReach23BeatStartFinishTogether(course, mss, m4m, existingMarks) {
  let marks = [];
  const numWindLegs = String(course.windwardBeats);
  // Reference point is m4m
  // TODO - calculate leg i.e. total length minus the leeward distance, finish distance and offset distance
  const totalLength = course.distance;
  // 60°, 120° = Spinnaker
  // 70°, 110° = Non-spinnaker
  const key = (course.spinnaker ? "60°, 120° " : "70°, 110° ") + course.finishType;
  const rLat = Number(m4m['lat']);
  const rLng = Number(m4m['lng']);

  marks = marks.concat(mark4(course, m4m));

  const m1Head = course.windDirection + trapezoid_course_angles[key]['angle-m4-m1'];
  const m1Dist = totalLength * trapezoid_course_angles[key]['I'][numWindLegs]['scale-m4-m1'];
  const m1m = createMark(rLat, rLng, m1Head, m1Dist, MARKS.M1, MARK_STATUS.LIFTED);
  if (course.gate1) {
    const m1sHead = course.windDirection + 90;
    const m1sDist = (course.averageBoatLength * course.gateWidth) / 2;
    marks.push(createMark(m1m['lat'], m1m['lng'], m1sHead, m1sDist, MARKS.M1S, MARK_STATUS.LIFTED));

    const m1pHead = course.windDirection - 90;
    const m1pDist = (course.averageBoatLength * course.gateWidth) / 2;
    marks.push(createMark(m1m['lat'], m1m['lng'], m1pHead, m1pDist, MARKS.M1P, MARK_STATUS.LIFTED));
  } else {
    marks.push(m1m);
    if (course.offset) {
      marks.push(createMark(m1m['lat'], m1m['lng'], course.windDirection - course.offsetAngle, course.offsetDistance, MARKS.M1A, MARK_STATUS.LIFTED));
    }
  }

  const m2Head = course.windDirection + trapezoid_course_angles[key]['angle-m4-m2'];
  const m2Dist = totalLength * trapezoid_course_angles[key]['I'][numWindLegs]['scale-m4-m2'];
  marks.push(createMark(rLat, rLng, m2Head, m2Dist, MARKS.M2, MARK_STATUS.LIFTED));

  const m3mHead = course.windDirection + trapezoid_course_angles[key]['angle-m4-m3'];
  const m3mDist = totalLength * trapezoid_course_angles[key]['I'][numWindLegs]['scale-m4-m3'];
  const m3m = createMark(rLat,rLng, m3mHead, m3mDist, MARKS.M3, MARK_STATUS.LIFTED);

  if (course.gate3) {
    const m3sHead = course.windDirection - 180.0 + 90;
    const m3sDist = (course.averageBoatLength * course.gateWidth) / 2;
    marks.push(createMark(m3m['lat'], m3m['lng'], m3sHead, m3sDist, MARKS.M3S, MARK_STATUS.LIFTED));

    const m3pHead = course.windDirection - 180.0 - 90;
    const m3pDist = (course.averageBoatLength * course.gateWidth) / 2;
    const m3p = createMark(m3m['lat'], m3m['lng'], m3pHead, m3pDist, MARKS.M3P, MARK_STATUS.LIFTED);
    marks.push(m3p);
  } else {
    marks.push(m3m);
  }

  const m5Head = course.windDirection + trapezoid_course_angles[key]['angle-m4-m5'];
  const m5Dist = totalLength * trapezoid_course_angles[key]['I'][numWindLegs]['scale-m4-m5'];
  const m5p = createMark(rLat, rLng, m5Head, m5Dist, MARKS.M5, MARK_STATUS.LIFTED);
  marks.push(m5p);

  // Create finish marks with EXACT coordinates from start line (start finish together)
  // This is an UPWIND finish, so tack designation stays same: SS→FS, SP→FP
  const ssMark = existingMarks.find(m => m.label === MARKS.SS_FP || m.label === MARKS.SS);
  const spMark = existingMarks.find(m => m.label === MARKS.SP_FS || m.label === MARKS.SP);
  
  if (!ssMark || !spMark) {
    console.error('Start line marks not found for finish line calculation (trapezoid windward)');
    return marks;
  }
  
  // UPWIND finish: tack designation stays same (SS→FS, SP→FP)
  marks.push(createMark(
    ssMark.lat, 
    ssMark.lng, 
    0,  // bearing doesn't matter - we're using exact lat/lng
    0,  // distance doesn't matter - we're using exact lat/lng
    MARKS.FS, 
    MARK_STATUS.LIFTED
  ));
  
  marks.push(createMark(
    spMark.lat, 
    spMark.lng, 
    0,  // bearing doesn't matter - we're using exact lat/lng
    0,  // distance doesn't matter - we're using exact lat/lng
    MARKS.FP, 
    MARK_STATUS.LIFTED
  ));

  return marks;
};
/**
 * Main course calculation function - matches CalculateCourse.js exactly
 * Returns marks in server format ready for database storage
 */
export const calculateCourse = (course) => {
  let marks = [];

  let startLineWidth = course.numberOfBoats * course.averageBoatLength * DEFAULT_STARTLINE_SCALE_FACTOR;
  let legs = calculateBeatLength(
    course.windDirection, 
    course.distance, 
    course.leewardDistance, 
    course.windwardBeats, 
    course.offset, 
    course.offsetAngle, 
    course.offsetDistance, 
    course.finishType, 
    course.finishDistance
  );
  
  let startMP = newMarkPosition(course.lat, course.lng, course.windDirection - 90, startLineWidth / 2);
  let m4m = newMarkPosition(startMP.lat, startMP.lng, course.windDirection, course.leewardDistance);

  marks = marks.concat(startLine(course, startLineWidth));

  if (course.type === COURSE_TYPES.T_LEEWARD || course.type === COURSE_TYPES.T_WINDWARD) {
    marks = marks.concat(mark1(course, startMP, legs));
    marks = marks.concat(mark4(course, m4m));
    marks = marks.concat(finishLine(course, startMP, m4m, legs, marks));
  } else if (course.type === COURSE_TYPES.T_TRIANGLE) {
    // Triangle course mark generation matches the sequence lookup:
    // 1 beat: S-1-F
    // 2 beats: S-1-2-4-1-F
    // 3 beats: S-1-2-4-1-2-4-1-F
    // etc.
    const numBeats = Math.max(1, course.windwardBeats || 1);
    const gateWidth = course.averageBoatLength * course.gateWidth;

    // Calculate fixed mark positions from start midpoint (same for every lap)
    const [mark1, mark2, mark4] = calculateTriangleCourseMarks(
      startMP.lat,
      startMP.lng,
      course.windDirection,
      course.distance,
      course.triangleAngle,
      course.leewardDistance,
      numBeats
    );

    // Always start with mark 1 (windward)
    marks.push(createMark(mark1.lat, mark1.lng, 0, 0, MARKS.M1, MARK_STATUS.LIFTED));

    // For each triangle loop (numBeats - 1 times), add 2, 4, 1
    for (let i = 0; i < numBeats - 1; i++) {
      marks.push(createMark(mark2.lat, mark2.lng, 0, 0, MARKS.M2, MARK_STATUS.LIFTED));
      if (course.gate4) {
        marks.push(createMark(mark4.lat, mark4.lng, course.windDirection + 90, gateWidth / 2, MARKS.M4S, MARK_STATUS.LIFTED));
        marks.push(createMark(mark4.lat, mark4.lng, course.windDirection - 90, gateWidth / 2, MARKS.M4P, MARK_STATUS.LIFTED));
      } else {
        marks.push(createMark(mark4.lat, mark4.lng, 0, 0, MARKS.M4, MARK_STATUS.LIFTED));
      }
      marks.push(createMark(mark1.lat, mark1.lng, 0, 0, MARKS.M1, MARK_STATUS.LIFTED));
    }

    // Finish line coincides with start line (shared start/finish — upwind finish)
    // Find the start line marks already in the array
    const ssMark = marks.find(m => m.label === MARKS.SS_FP || m.label === MARKS.SS);
    const spMark = marks.find(m => m.label === MARKS.SP_FS || m.label === MARKS.SP);
    if (ssMark && spMark) {
      marks.push(createMark(ssMark.lat, ssMark.lng, 0, 0, MARKS.FS, MARK_STATUS.LIFTED));
      marks.push(createMark(spMark.lat, spMark.lng, 0, 0, MARKS.FP, MARK_STATUS.LIFTED));
    }
  } else {
    if (course.finishType === FO_IO_SFTRHB_L) {
      marks = marks.concat(calculateTrapezoidStartFinishReachHalfBeat(course, marks[0], startMP, m4m, legs));
    } else if (course.finishType === FO_IO_SSFRHB_L) {
      marks = marks.concat(calculateTrapezoidSeparateStartFinishReachHalfBeat(course, marks[0], m4m));
    } else if (course.finishType === FO_IO_EBRHB_L) {
      marks = marks.concat(calculateTrapezoidEqualBeatsReachHalf(course, marks[0], m4m));
    } else if (course.finishType === FO_IO_R23B_L) {
      marks = marks.concat(calculateTrapezoidReach23Beat(course, marks[0], m4m));
    } else if (course.finishType === FO_IO_WEBR23B_L) {
      marks = marks.concat(calculateWindwardEqualBeatsReach23Beat(course, marks[0], m4m));
    } else if (course.finishType === FO_IO_WEBR23BSF) {
      marks = marks.concat(calculateWindwardEqualBeatsReach23BeatStartFinishTogether(course, marks[0], m4m, marks));
    } else {
      console.error("Should not end up here switch course.finishType");
      console.error("Received finishType:", course.finishType);
      console.error("Expected one of:", [FO_IO_SFTRHB_L, FO_IO_SSFRHB_L, FO_IO_EBRHB_L, FO_IO_R23B_L, FO_IO_WEBR23B_L, FO_IO_WEBR23BSF]);
      return [];
    }
  }

  // Convert marks to server format (combine gates and lines)
  return convertMarksToServerFormat(marks);
};

/**
 * Validate course configuration data
 */
export const validateCourseConfiguration = (data) => {
  const errors = [];
  
  if (!data.lat || !data.lng) {
    errors.push('Start position (latitude/longitude) is required');
  }
  
  if (!data.numberOfBoats || data.numberOfBoats < 1) {
    errors.push('Number of boats must be at least 1');
  }
  
  if (!data.averageBoatLength || data.averageBoatLength < 1) {
    errors.push('Average boat length must be at least 1 meter');
  }
  
  if (data.windDirection < 0 || data.windDirection > 360) {
    errors.push('Wind direction must be between 0 and 360 degrees');
  }
  
  if (!data.distance || data.distance <= 0) {
    errors.push('Course length must be greater than 0');
  }
  
  if (!data.leewardDistance || data.leewardDistance <= 0) {
    errors.push('Leeward distance must be greater than 0');
  }
  
  if (!data.windwardBeats || data.windwardBeats < 1 || data.windwardBeats > 4) {
    errors.push('Windward beats must be between 1 and 4');
  }
  
  return errors;
};

export default calculateCourse;